// The staff-administration route subtree, hung off the ADMIN portal's root route
// so router.tsx stays the single wiring point. Parent-generic (<TParent>) so the
// module never names a portal's Register-typed paths — the portability law the
// Law F1 migration passes established.
import { createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import type { AnyRoute } from "@tanstack/solid-router";

/**
 * The register's URL contract — self-healing, like every other list screen's.
 *
 * NO CURSOR (2026-09-23). The register streams: pages accumulate through
 * `useInfiniteQuery` over the same keyset endpoint, so a cursor is a position
 * INSIDE the stream rather than part of the view's identity, and a link
 * carrying one would restore a middle page with no way back to the top. The
 * facets, the role and the search stayed — a deep link still restores exactly
 * what the reader was looking at.
 */
export interface StaffSearch {
  search?: string;
  role?: string;
  verified?: "yes" | "no";
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v.length > 0 ? v : undefined);

export function validateStaffSearch(input: Record<string, unknown>): StaffSearch {
  return {
    search: str(input.search),
    // The role vocabulary is auth-kit's; anything else heals to "all roles"
    // rather than throwing, so a stale or hand-typed link still renders.
    role: str(input.role),
    verified: input.verified === "yes" || input.verified === "no" ? input.verified : undefined,
  };
}

/** The role register's URL contract. */
export interface RolesSearch {
  search?: string;
  group?: "platform" | "operator" | "member";
  kind?: "builtin" | "custom";
}

const GROUPS = ["platform", "operator", "member"] as const;
const KINDS = ["builtin", "custom"] as const;

export function validateRolesSearch(input: Record<string, unknown>): RolesSearch {
  return {
    search: str(input.search),
    group: GROUPS.includes(input.group as (typeof GROUPS)[number]) ? (input.group as RolesSearch["group"]) : undefined,
    kind: KINDS.includes(input.kind as (typeof KINDS)[number]) ? (input.kind as RolesSearch["kind"]) : undefined,
  };
}

export function makeAuthStaffRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const rolesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "staff/roles",
    validateSearch: validateRolesSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/RolesList.js")));

  const listRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "staff",
    validateSearch: validateStaffSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/StaffList.js")));

  const inviteRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "staff/invite",
  }).lazy(lazyRoute(() => import("../../screens/admin/InviteStaff.js")));

  return [listRoute, rolesRoute, inviteRoute];
}
